import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { LearningState, LearningActions } from '../types';
import { getApiService } from '../api/services';

interface LearningStore extends LearningState {
  actions: LearningActions;
}

export const useLearningStore = create<LearningStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      todaysWords: [],
      currentSessionToken: null,
      currentWordIndex: 0,
      completedWordsIds: [],
      isSessionActive: false,
      sessionExpiresAt: null,
      isLoading: false,
      error: null,

      // Actions
      actions: {
        setSessionToken: (token: string | null) => {
          set(
            {
              currentSessionToken: token,
              isSessionActive: !!token,
            },
            false,
            'learning/setSessionToken'
          );
        },

        loadTodaysWords: async (token: string) => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'learning/loadTodaysWords/start'
            );

            const apiService = getApiService();
            const data = await apiService.getTodaysWords(token);

            set(
              {
                todaysWords: data.words,
                currentSessionToken: token,
                isSessionActive: true,
                isLoading: false,
                error: null,
              },
              false,
              'learning/loadTodaysWords/success'
            );
          } catch (error) {
            console.log('>>>>>>>>>> error', error);
            set(
              {
                isLoading: false,
                error,
              },
              false,
              'learning/loadTodaysWords/error'
            );
            throw error;
          }
        },

        startSession: async (token: string) => {
          try {
            set(
              { isLoading: true, error: null },
              false,
              'learning/startSession/start'
            );

            const apiService = getApiService();
            const { expiresAt } = await apiService.startSession(token);

            set(
              {
                currentSessionToken: token,
                isSessionActive: true,
                sessionExpiresAt: expiresAt,
                isLoading: false,
                error: null,
              },
              false,
              'learning/startSession/success'
            );
          } catch (error) {
            set(
              {
                isLoading: false,
                error,
              },
              false,
              'learning/startSession/error'
            );
            throw error;
          }
        },

        markWordAsLearned: async (token: string, wordId: string) => {
          const { completedWordsIds, currentWordIndex, todaysWords } = get();

          try {
            // Optimistic update
            const newCompletedIds = [...completedWordsIds, wordId];
            const nextIndex = Math.min(
              currentWordIndex + 1,
              todaysWords.length - 1
            );

            set(
              {
                completedWordsIds: newCompletedIds,
                currentWordIndex: nextIndex,
                isLoading: true,
                error: null,
              },
              false,
              'learning/markWordAsLearned/optimistic'
            );

            const apiService = getApiService();
            const result = await apiService.markWordAsLearned(token, wordId);

            set(
              {
                isLoading: false,
                error: null,
                sessionExpiresAt: result.expiresAt || get().sessionExpiresAt,
              },
              false,
              'learning/markWordAsLearned/success'
            );

            // If all words are completed, session is over
            if (result.allWordsCompleted) {
              set(
                {
                  isSessionActive: false,
                },
                false,
                'learning/markWordAsLearned/completed'
              );
            }
          } catch (error) {
            // Revert optimistic update
            set(
              {
                completedWordsIds,
                currentWordIndex,
                isLoading: false,
                error,
              },
              false,
              'learning/markWordAsLearned/error'
            );
            throw error;
          }
        },

        expireSession: async (token: string) => {
          try {
            set({ isLoading: true }, false, 'learning/expireSession/start');

            const apiService = getApiService();
            await apiService.expireToken(token);

            // Clear session state
            set(
              {
                currentSessionToken: null,
                isSessionActive: false,
                sessionExpiresAt: null,
                currentWordIndex: 0,
                completedWordsIds: [],
                todaysWords: [],
                isLoading: false,
                error: null,
              },
              false,
              'learning/expireSession/success'
            );
          } catch (error) {
            set(
              {
                isLoading: false,
                error,
              },
              false,
              'learning/expireSession/error'
            );

            // Still clear session state even if API fails
            set(
              {
                currentSessionToken: null,
                isSessionActive: false,
                sessionExpiresAt: null,
                currentWordIndex: 0,
                completedWordsIds: [],
                todaysWords: [],
              },
              false,
              'learning/expireSession/clear'
            );
          }
        },

        setCurrentWordIndex: (index: number) => {
          const { todaysWords } = get();
          const clampedIndex = Math.max(
            0,
            Math.min(index, todaysWords.length - 1)
          );
          set(
            { currentWordIndex: clampedIndex },
            false,
            'learning/setCurrentWordIndex'
          );
        },

        resetSession: () => {
          set(
            {
              todaysWords: [],
              currentSessionToken: null,
              currentWordIndex: 0,
              completedWordsIds: [],
              isSessionActive: false,
              sessionExpiresAt: null,
              isLoading: false,
              error: null,
            },
            false,
            'learning/resetSession'
          );
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, 'learning/setLoading');
        },

        setError: (error: string | Error | unknown | null) => {
          set({ error }, false, 'learning/setError');
        },
      },
    }),
    {
      name: 'learning-store',
    }
  )
);
